'use client'

import { useState } from 'react'
import Link from 'next/link'
import { BookOpen, MonitorPlay, ArrowRight, CheckCircle2, History, Microscope, Calculator, Brain, GraduationCap } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'

type ExamType = 'neet' | 'jee'

const EXAMS = [
  {
    id: 'neet' as ExamType,
    name: 'NEET',
    icon: <Microscope className="w-8 h-8" />,
    gradient: 'from-green-500 via-emerald-500 to-teal-500',
    color: 'text-green-600',
    bg: 'bg-green-50 dark:bg-green-900/20'
  },
  {
    id: 'jee' as ExamType,
    name: 'JEE',
    icon: <Calculator className="w-8 h-8" />,
    gradient: 'from-blue-500 via-cyan-500 to-teal-500',
    color: 'text-blue-600',
    bg: 'bg-blue-50 dark:bg-blue-900/20'
  }
]

export function PYQsSection() {
  const [selectedExam, setSelectedExam] = useState<ExamType | null>(null)

  const handleExamChange = (value: string) => {
    setSelectedExam(value as ExamType)
  }

  const handleExamModeClick = (e: React.MouseEvent) => {
    // Optionally show a toast if Exam Mode is not yet fully implemented
    // toast.info("Exam Mode is coming soon! For now, you will be redirected to the standard practice interface.")
  }

  return (
    <section className="relative py-20 bg-white dark:bg-gray-900 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-semibold text-sm border border-blue-100 dark:border-blue-800">
            <History className="w-4 h-4" />
            Previous Year Questions
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 dark:text-white">
            Master the <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Actual Exams</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Practice past year papers exactly how you want. Choose your exam to get started.
          </p>
        </div>

        {/* Step 1: Select Exam */}
        <div className="max-w-sm mx-auto mb-12">
          <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2 text-center">
            Step 1: Select Your Exam
          </label>
          <Select value={selectedExam || ''} onValueChange={handleExamChange}>
            <SelectTrigger className="w-full h-14 text-lg bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500">
              <SelectValue placeholder="Choose an exam..." />
            </SelectTrigger>
            <SelectContent>
              {EXAMS.map((exam) => (
                <SelectItem key={exam.id} value={exam.id} className="py-3 cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className={`p-1.5 rounded-lg ${exam.bg} ${exam.color}`}>
                      {exam.icon}
                    </div>
                    <span className="font-semibold">{exam.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Step 2: Select Mode (Animates in when exam is selected) */}
        <div className={`transition-all duration-500 ease-in-out ${selectedExam ? 'opacity-100 translate-y-0 max-h-[800px]' : 'opacity-0 -translate-y-4 max-h-0 overflow-hidden'}`}>
          <div className="pt-8 border-t border-gray-100 dark:border-gray-800">
            <h3 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-8">
              Select Practice Mode
            </h3>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              
              {/* Practice Mode Card */}
              <div className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-200 dark:border-gray-800 p-8 shadow-sm hover:shadow-xl transition-all group">
                <div className="w-16 h-16 rounded-2xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <BookOpen className="w-8 h-8" />
                </div>
                <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Practice Mode</h4>
                <p className="text-gray-600 dark:text-gray-400 mb-6 min-h-[48px]">
                  Take your time. Perfect for learning and building concepts chapter by chapter.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-blue-500" /> Practice chapter-wise & topic-wise
                  </li>
                  <li className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-blue-500" /> View detailed step-by-step solutions
                  </li>
                  <li className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-blue-500" /> Retry questions anytime
                  </li>
                  <li className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-blue-500" /> No timer restrictions
                  </li>
                </ul>
                <Link href={`/practice-exams?exam=${selectedExam}`}>
                  <button className="w-full py-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold flex items-center justify-center gap-2 transition-colors">
                    Start Practice <ArrowRight className="w-5 h-5" />
                  </button>
                </Link>
              </div>

              {/* Exam Mode Card */}
              <div className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-200 dark:border-gray-800 p-8 shadow-sm hover:shadow-xl transition-all group relative overflow-hidden">
                {/* Subtle gradient background for premium feel */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent pointer-events-none" />
                
                <div className="w-16 h-16 rounded-2xl bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform relative z-10">
                  <MonitorPlay className="w-8 h-8" />
                </div>
                <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 relative z-10">Exam Mode</h4>
                <p className="text-gray-600 dark:text-gray-400 mb-6 min-h-[48px] relative z-10">
                  Real NTA Computer Based Test simulation. Best for final revision and time management.
                </p>
                <ul className="space-y-3 mb-8 relative z-10">
                  <li className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-purple-500" /> Exact NTA CBT Interface
                  </li>
                  <li className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-purple-500" /> Full Screen & Strict Timer
                  </li>
                  <li className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-purple-500" /> Save, Review, Palette features
                  </li>
                  <li className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-purple-500" /> Auto-result generation
                  </li>
                </ul>
                <Link href={`/practice-exams?exam=${selectedExam}&mode=exam`} onClick={handleExamModeClick}>
                  <button className="w-full py-4 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold flex items-center justify-center gap-2 transition-colors relative z-10">
                    Practice in CBT Mode <ArrowRight className="w-5 h-5" />
                  </button>
                </Link>
              </div>

            </div>
          </div>
        </div>
        
      </div>
    </section>
  )
}
